import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import OptimizedImage from "@/components/OptimizedImage";

interface ProblemSolutionProps {
  onSignIn: () => void;
}

const ProblemSolution: React.FC<ProblemSolutionProps> = ({ onSignIn }) => {
  return (
    <section className="w-full py-12 sm:py-16 lg:py-20 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10" />



      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Main Heading */}
          <div className="text-center sm:text-left mb-4 sm:mb-6">
            <h1 className="text-xs sm:text-sm md:text-base font-bold text-foreground/90 uppercase tracking-wider">
              Problems & Solution
            </h1>
          </div>

          {/* Problem Section */}
          <ScrollReveal direction="up" delay={0}>
            <div className="text-center sm:text-left mb-12 sm:mb-16 md:mb-20">
              <div className="flex flex-col lg:flex-row items-center sm:items-start lg:items-end gap-6 lg:gap-24">
                <div className="flex items-end gap-0 w-full lg:w-auto justify-center sm:justify-start">
                  <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                    Stop <span className="text-primary">juggling tools</span>.<br />
                    <span className="inline-flex items-end gap-2 sm:gap-3 md:gap-4">
                      Start <span className="text-primary">ranking</span>.
                      <OptimizedImage
                        src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/sign/random%20images/arrow-right-black.538c912a.svg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xOWQwMmZkMy05MzllLTQ3YWYtODEyYS0zOWQxMTc0NGY2N2IiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJyYW5kb20gaW1hZ2VzL2Fycm93LXJpZ2h0LWJsYWNrLjUzOGM5MTJhLnN2ZyIsImlhdCI6MTc2Mzg1MTIwOSwiZXhwIjoxOTIxNTMxMjA5fQ.bBnvaFY4fcPXFcUI3k4ZcGcVmjjZsJVbgvuLZpQNQXI"
                        alt=""
                        width={24}
                        height={24}
                        className="h-3 sm:h-4 md:h-5 lg:h-6 w-auto hidden sm:block flex-shrink-0 mb-1 sm:mb-1.5"
                        aria-hidden="true"
                      />
                    </span>
                  </h2>
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* Solution Section */}
          <div className="mb-16 sm:mb-20">

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto items-stretch">
              <div className="flex items-center justify-center h-full order-2 lg:order-1">
                <OptimizedImage
                  src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/solutions-desktop.d8617a4b.svg"
                  alt="Solutions Platform"
                  width={800}
                  height={600}
                  className="w-full max-w-4xl mx-auto h-auto"
                />
              </div>
              <div className="flex items-center justify-center h-full order-1 lg:order-2">
                                <div className="relative bg-primary backdrop-blur-lg rounded-2xl border border-primary/30 shadow-xl p-4 sm:p-6 w-full max-w-7xl lg:ml-6 ml-0 overflow-hidden hover:bg-primary/95 transition-all duration-300 group">
                  {/* Elegant shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shine" style={{ animationDuration: '3s', animationIterationCount: 'infinite', animationTimingFunction: 'ease-in-out' }}></div>

                  {/* Logo and Text Section - ON GRADIENT */}
                  <div className="relative flex flex-row items-center space-x-4 mb-6">
                    <div className="relative">
                      <div className="h-16 w-16">
                        <OptimizedImage
                          src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/Untitled-222.png"
                          alt="Seoro Logo"
                          width={64}
                          height={64}
                          priority={true}
                          className="h-full w-full object-contain filter brightness-0 invert"
                        />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-primary/3 rounded-lg -z-10 opacity-0 filter blur-15px"></div>
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                      Seoro
                    </h3>
                  </div>

                  {/* Inner white card with feature list */}
                  <div className="relative bg-white/95 backdrop-blur-md rounded-xl border border-gray-200/40 shadow-lg p-4 sm:p-5 flex flex-col h-full">
                    <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-4 sm:mb-6 leading-relaxed font-medium text-left">
                      Everything you need to dominate search results:
                    </p>
                    <div className="grid grid-cols-1 gap-3 sm:gap-4 text-left">
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0" />
                          <div className="absolute inset-0 bg-primary/20 rounded-full scale-0"></div>
                        </div>
                        <span className="text-foreground font-medium text-sm sm:text-base md:text-lg">Topic Clusters & Ideation</span>
                      </div>
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="relative">
                          <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-primary flex-shrink-0" />
                          <div className="absolute inset-0 bg-primary/20 rounded-full scale-0"></div>
                        </div>
                        <span className="text-foreground font-medium text-sm sm:text-base md:text-lg">AI Article Writing</span>
                      </div>
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="relative">
                          <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-primary flex-shrink-0" />
                          <div className="absolute inset-0 bg-primary/20 rounded-full scale-0"></div>
                        </div>
                        <span className="text-foreground font-medium text-sm sm:text-base md:text-lg">Google Search Console Integration</span>
                      </div>
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="relative">
                          <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-primary flex-shrink-0" />
                          <div className="absolute inset-0 bg-primary/20 rounded-full scale-0"></div>
                        </div>
                        <span className="text-foreground font-medium text-sm sm:text-base md:text-lg">AI Image Generation</span>
                      </div>
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="relative">
                          <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-primary flex-shrink-0" />
                          <div className="absolute inset-0 bg-primary/20 rounded-full scale-0"></div>
                        </div>
                        <span className="text-foreground font-medium text-sm sm:text-base md:text-lg">LLM.txt Support</span>
                      </div>
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="relative">
                          <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-primary flex-shrink-0" />
                          <div className="absolute inset-0 bg-primary/20 rounded-full scale-0"></div>
                        </div>
                        <span className="text-foreground font-medium text-sm sm:text-base md:text-lg">Monthly Backlinks (DA 70+)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>


        </div>
      </div>
    </section>
  );
};

export default ProblemSolution;
